import { otherUserToMessageSearch } from "./otherUserToMessageSearch";

/**
 * Filters results to unique cids where the message user is the logged-in user.
 */
function getUniqueOwnMessageResults(results, userId) {
  const seenCids = new Set();
  return results.filter(result => {
    const isOwnMessage = result.message.user.id === userId;
    if (!isOwnMessage) return false;
    if (seenCids.has(result.message.cid)) return false;
    seenCids.add(result.message.cid);
    return true;
  });
}
/**
 * Searches for messages in direct (1:1) messaging channels where the specified user is a member.
 *
 * @param {Object} client - The chat client instance.
 * @param {string} query - The search string to match against a message text.
 * @param {string} userId - The ID of the user performing the search.
 * @param {Object} options - Pagination and query options ({ limit, offset, etc. }).
 * @returns {Promise<Array>} - Array of matching message results.
 *
 * How it works:
 * - Filters channels of type 'messaging' with exactly two members (direct messages).
 * - The user must be a member of the channel.
 * - Restricts results to the user's first team.
 * - Searches for messages where the text matches the query (autocomplete).
 * - Supports pagination via limit and offset.
 */
export const messageSearch = async (client, query, userId, options) => {
  const searchChannelFilters = {
    type: "messaging",
    member_count: { $eq: 2 },
    members: { $in: [userId] },
    team: { $eq: client.user.teams[0] }
  };
  const searchMessageFilters = { text: { $autocomplete: query } };
  const { results } = await client.search(
    searchChannelFilters,
    searchMessageFilters,
    {
      limit: options.limit,
      offset: options.offset,
      watch: true,
      state: true
    }
  );

  if (!results?.length) return [];


  // Only process unique cids where the message user is the logged-in user
  const uniqueOwnMessageResults = getUniqueOwnMessageResults(results, userId);

  // Transform results sent by the user using otherUserToMessageSearch.
  await Promise.all(
    uniqueOwnMessageResults.map(result =>
      otherUserToMessageSearch(client, result, userId)
    )
  );

  return results;
};